/**
 * As chamadas da tela de faturamento (`TP-052`, T9).
 *
 * Tudo o que a tela sabe sobre `/faturamento/conferencias` passa por aqui —
 * mesma disciplina de `features/contratos/api/contratos.ts`.
 */

import { queryOptions } from '@tanstack/react-query';

import { requisitar, requisitarComStatus } from '../../../shared/api/clienteHttp';
import { ACAO_PARA_API, mapearConferenciaImportada, mapearConferenciaResumo, mapearDetalheDaConferencia, mapearEstadoDaConferencia } from './mapeadores';
import type {
  AcaoDeDecisao,
  ConferenciaImportada,
  ConferenciaImportadaApi,
  ConferenciaResumo,
  ConferenciaResumoApi,
  DecisaoResponseApi,
  DetalheConferenciaApi,
  DetalheDaConferencia,
  EstadoDaConferencia,
} from './tipos';

export const chavesDeFaturamento = {
  raiz: ['faturamento'] as const,
  lista: (contratoId: string) => [...chavesDeFaturamento.raiz, 'lista', contratoId] as const,
  conferencia: (conferenciaId: string) => [...chavesDeFaturamento.raiz, 'conferencia', conferenciaId] as const,
};

/** `contratoId` vazio pede TODOS os contratos — `GET /faturamento/conferencias` sem o filtro. */
export function opcoesDasConferencias(contratoId: string) {
  return queryOptions({
    queryKey: chavesDeFaturamento.lista(contratoId),
    queryFn: ({ signal }): Promise<ConferenciaResumo[]> => {
      const parametros = new URLSearchParams();
      if (contratoId.trim() !== '') parametros.set('contratoId', contratoId.trim());
      const querystring = parametros.toString();
      return requisitar<ConferenciaResumoApi[]>(
        `/faturamento/conferencias${querystring !== '' ? `?${querystring}` : ''}`,
        { sinal: signal },
      ).then((itens) => itens.map(mapearConferenciaResumo));
    },
  });
}

export function opcoesDaConferencia(conferenciaId: string) {
  return queryOptions({
    queryKey: chavesDeFaturamento.conferencia(conferenciaId),
    queryFn: ({ signal }): Promise<DetalheDaConferencia> =>
      requisitar<DetalheConferenciaApi>(`/faturamento/conferencias/${encodeURIComponent(conferenciaId)}`, {
        sinal: signal,
      }).then(mapearDetalheDaConferencia),
    enabled: conferenciaId !== '',
  });
}

/**
 * `POST /faturamento/conferencias`, `multipart/form-data` (T8/T9). `criada`
 * vem do STATUS — `200` é a conferência que já existia pelo (contrato, hash),
 * `201` é conferência nova (mesmo padrão de `enviarFonte`, `D6` do TP-049).
 */
export async function importarConferencia(parametros: {
  contratoId: string;
  arquivo: File;
}): Promise<{ conferencia: ConferenciaImportada; criada: boolean }> {
  const forma = new FormData();
  forma.append('contratoId', parametros.contratoId);
  forma.append('file', parametros.arquivo);

  const { dado, status } = await requisitarComStatus<ConferenciaImportadaApi>('/faturamento/conferencias', {
    method: 'POST',
    corpo: forma,
  });

  return { conferencia: mapearConferenciaImportada(dado), criada: status === 201 };
}

/** `POST /faturamento/conferencias/{id}/decisoes` (T8/T9, `D11`): uma decisão por linha BARRADA, motivo obrigatório. */
export async function decidirLinha(parametros: {
  conferenciaId: string;
  avaliacaoId: string;
  linha: number;
  acao: AcaoDeDecisao;
  motivo: string;
}): Promise<EstadoDaConferencia> {
  const resposta = await requisitar<DecisaoResponseApi>(
    `/faturamento/conferencias/${encodeURIComponent(parametros.conferenciaId)}/decisoes`,
    {
      method: 'POST',
      corpo: {
        avaliacaoId: parametros.avaliacaoId,
        linha: parametros.linha,
        acao: ACAO_PARA_API[parametros.acao],
        motivo: parametros.motivo,
      },
    },
  );
  return mapearEstadoDaConferencia(resposta.estado);
}
